import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../AuthComponent';
import { useNavigate } from 'react-router-dom';
import { Banner, Button, FormLayout, Select, Text, TextField } from '@shopify/polaris';
import { itApi } from './apiClient';
import { ChargesEditor, ItemsEditor, blankItem, chargesTotal, countable, fromApiCharges, fromAsset, fromLine, fromRead, problems, toApiCharges, toApiItems, toEditBody } from './ReceiptItems';
import { Alert, DateField, Field, GONE, KIND_LABEL, LocChip, Modal, StatusBadge, WhereCell, WhoCell, dateOnly, dueBadge, money, plural, tagText, useIt, when } from './common';
import { StatusPill } from '../ui';
import { PageStrip, PageViewer, ReceiptPages, asNew, newRows, pagesText, storedRows, useAction, useNewPages, useStoredPages } from './ReceiptPages';

const stores = (meta) => meta.locations.filter((l) => l.kind === 'store');

// ── people ───────────────────────────────────────────────────────────────────

export function AddPersonDialog({ name: initial = '', onClose, onAdded }) {
  const { meta, reloadMeta, notify } = useIt();
  const [name, setName] = useState(initial);
  const [email, setEmail] = useState('');
  const [loc, setLoc] = useState('');
  const [near, setNear] = useState(null);
  const { busy, error, run } = useAction();
  const save = (force) => run(async () => {
    const r = await itApi.addPerson({ name, email, location_id: loc ? Number(loc) : null, force });
    if (!r.ok) { setNear(r.lookalikes); return; }
    await reloadMeta();
    notify(`${name} added`);
    onAdded && onAdded({ id: r.id, name });
    onClose();
  });
  return (
    <Modal title="Add a person" onClose={onClose} footer={<>
      <button type="button" className="uh-btn" onClick={onClose}>Cancel</button>
      {near
        ? <button type="button" className="uh-btn primary" disabled={busy} onClick={() => save(true)}>Add anyway</button>
        : <button type="button" className="uh-btn primary" disabled={busy || !name.trim() || !email.trim()} onClick={() => save(false)}>Save person</button>}
    </>}>
      <Alert>{error}</Alert>
      <div className="uh-ia-frow">
        <Field label="Full name" required><input className="uh-ia-inp" value={name} onChange={(e) => { setName(e.target.value); setNear(null); }} /></Field>
        <Field label="Email" required hint="Tells two people with the same name apart."><input className="uh-ia-inp" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@soccerpost.com" /></Field>
        <Field label="Works at">
          <select className="uh-select" value={loc} onChange={(e) => setLoc(e.target.value)}>
            <option value="">Not set</option>{meta.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </Field>
      </div>
      {near && (
        <div className="uh-ia-warn" role="status">
          Did you mean {near.map((p) => `${p.name} (${p.email})`).join(' or ')}? Check before adding a duplicate.
        </div>
      )}
    </Modal>
  );
}

export function RemovePersonDialog({ person, onClose }) {
  const { reloadMeta, refresh, notify } = useIt();
  const navigate = useNavigate();
  const { busy, error, run } = useAction();
  const held = person.devices || 0;
  const go = () => run(async () => { await itApi.removePerson(person.id); await reloadMeta(); notify(`Removed ${person.name}`); refresh(); onClose(); });
  const leave = () => run(async () => { await itApi.editPerson(person.id, { left: true }); await reloadMeta(); notify(`${person.name} marked as left`); refresh(); onClose(); });
  return (
    <Modal title={`Remove ${person.name}`} onClose={onClose} footer={<>
      <button type="button" className="uh-btn" onClick={onClose}>Cancel</button>
      {held > 0 && !person.left_on && <button type="button" className="uh-btn" disabled={busy} onClick={leave}>Mark as left instead</button>}
      <button type="button" className="uh-btn danger" disabled={busy || held > 0} onClick={go}>Remove</button>
    </>}>
      <Alert>{error}</Alert>
      {held > 0 ? (
        <>
          <div className="uh-ia-warn" role="status">{person.name} still holds {plural(held, 'device', 'devices')}. Check them in, or check them out to someone else, before removing. Marking {person.name} as left keeps them in the list and shows what is still out.</div>
          <p><button type="button" className="uh-link" onClick={() => { onClose(); navigate(`/it-assets/assets?q=${encodeURIComponent(person.name)}`); }}>Open {person.name}'s devices on the Assets tab</button></p>
        </>
      ) : (
        <p>{person.name} holds no devices. Past check-outs stay in each device's history, so nothing else points at them; they are taken out of the list and every picker.</p>
      )}
    </Modal>
  );
}

// ── locations ────────────────────────────────────────────────────────────────

const LOCATION_KINDS = Object.keys(KIND_LABEL);

export function AddLocationDialog({ onClose, onAdded, initialKind = 'store' }) {
  const { reloadMeta, refresh, notify } = useIt();
  const [name, setName] = useState('');
  const [kind, setKind] = useState(initialKind);
  const { busy, error, run } = useAction();
  const save = () => run(async () => {
    const r = await itApi.addLocation({ name: name.trim(), kind });
    await reloadMeta(); notify(`Added ${name.trim()}`); refresh();
    onAdded && onAdded({ id: r.id, name: name.trim(), kind });
    onClose();
  });
  return (
    <Modal title="Add a location" onClose={onClose} footer={<>
      <button type="button" className="uh-btn" onClick={onClose}>Cancel</button>
      <button type="button" className="uh-btn primary" disabled={busy || !name.trim()} onClick={save}>Add</button>
    </>}>
      <Alert>{error}</Alert>
      <div className="uh-ia-frow">
        <Field label="Name" required><input className="uh-ia-inp" value={name} onChange={(e) => setName(e.target.value)} placeholder="Eatontown Tech Inventory" autoFocus onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) save(); }} /></Field>
        <Field label="Type" hint="Home office is someone's home. Inventory is a shelf of devices between uses, like the tech team's own stock. Store and Teams are places that sell.">
          <select className="uh-select" value={kind} onChange={(e) => setKind(e.target.value)}>{LOCATION_KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}</select>
        </Field>
      </div>
    </Modal>
  );
}

export function RemoveLocationDialog({ location: l, onClose }) {
  const { meta, reloadMeta, refresh, notify } = useIt();
  const [to, setTo] = useState('');
  const { busy, error, run } = useAction();
  const parts = [l.devices ? plural(l.devices, 'asset', 'assets') : '', l.people ? `${l.people} ${l.people === 1 ? 'person works' : 'people work'} here` : '',
    l.events ? plural(l.events, 'event', 'events') : '', l.lines ? plural(l.lines, 'receipt line', 'receipt lines') : ''].filter(Boolean);
  const busyLoc = parts.length > 0;
  const single = (l.devices || 0) + (l.people || 0) + (l.events || 0) + (l.lines || 0) === 1;
  const others = meta.locations.filter((o) => o.id !== l.id);
  const go = () => run(async () => {
    const r = await itApi.removeLocation(l.id, busyLoc ? Number(to) : null);
    await reloadMeta(); refresh();
    notify(`Removed ${l.name}${r.to ? `, everything moved to ${r.to}` : ''}`);
    onClose();
  });
  return (
    <Modal title={`Remove ${l.name}`} onClose={onClose} footer={<>
      <button type="button" className="uh-btn" onClick={onClose}>Cancel</button>
      <button type="button" className="uh-btn danger" disabled={busy || (busyLoc && !to)} onClick={go}>Remove</button>
    </>}>
      <Alert>{error}</Alert>
      {busyLoc ? (
        <>
          <div className="uh-ia-warn" role="status">{parts.join(', ')} still {single ? 'points' : 'point'} at {l.name}. Pick where {single ? 'it belongs' : 'they belong'} and the Hub moves {single ? 'it' : 'them'} before the name is retired.</div>
          <dl className="uh-ia-kv">
            <dt>Assets</dt><dd>{l.devices} <span className="uh-faint">({l.deployed} deployed, {l.on_loan} on loan, {l.in_stock} in stock)</span></dd>
            <dt>People</dt><dd>{l.people}</dd><dt>Events</dt><dd>{l.events}</dd><dt>Receipt lines</dt><dd>{l.lines}</dd>
          </dl>
          <Field label="Move everything to" required hint="A retired location can't be picked. Assets keep their status and holder; only the place changes.">
            <select className="uh-select" value={to} onChange={(e) => setTo(e.target.value)}>
              <option value="">Pick a location…</option>{others.map((o) => <option key={o.id} value={o.id}>{o.name} ({KIND_LABEL[o.kind] || o.kind})</option>)}
            </select>
          </Field>
        </>
      ) : <p>Nothing points at {l.name}. It leaves every dropdown now; the name stays on anything historical.</p>}
    </Modal>
  );
}

export function PersonPicker({ value, onChange, id = 'ia-person', initial = '', placeholder = 'Start typing a name' }) {
  const { meta, reloadMeta } = useIt();
  const [q, setQ] = useState(value ? value.name : initial);
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [ws, setWs] = useState([]);
  const matches = useMemo(() => {
    const t = q.trim().toLowerCase();
    return meta.people.filter((p) => !p.left_on && (!t || p.name.toLowerCase().includes(t) || p.email.toLowerCase().includes(t))).slice(0, 6);
  }, [q, meta.people]);
  // Workspace accounts that are not device users yet; picking one adds them
  useEffect(() => {
    const t = q.trim();
    if (value || t.length < 2) { setWs([]); return undefined; }
    const timer = setTimeout(() => itApi.workspaceUsers(t).then((r) => setWs(r.users.filter((u) => !meta.people.some((p) => p.email.toLowerCase() === u.email)))).catch(() => setWs([])), 200);
    return () => clearTimeout(timer);
  }, [q, value, meta.people]);
  const pickWorkspace = async (u) => {
    const r = await itApi.personFromWorkspace(u.email);
    onChange(r.person); setQ(r.person.name); setOpen(false); reloadMeta();
  };
  return (
    <div className="uh-ia-picker">
      <input id={id} className="uh-ia-inp" value={q} autoComplete="off" placeholder={placeholder}
        onChange={(e) => { setQ(e.target.value); onChange(null); setOpen(true); }} onFocus={() => setOpen(true)} />
      {value && <span className="uh-ia-picked">{value.email}</span>}
      {open && !value && q.trim() && (
        <div className="uh-ia-pop">
          {matches.map((p) => (
            <button type="button" key={p.id} onClick={() => { onChange(p); setQ(p.name); setOpen(false); }}>
              {p.name} <span className="uh-faint">{p.email}</span>
            </button>
          ))}
          {ws.map((u) => (
            <button type="button" key={u.email} onClick={() => pickWorkspace(u)}>
              {u.name} <span className="uh-faint">{u.email}</span> <span className="uh-ia-sm">Google Workspace</span>
            </button>
          ))}
          <button type="button" className="add" onClick={() => setAdding(true)}>Add “{q.trim()}” as a new person, not in Workspace</button>
        </div>
      )}
      {adding && <AddPersonDialog name={q.trim()} onClose={() => setAdding(false)} onAdded={(p) => { onChange(p); setQ(p.name); setOpen(false); }} />}
    </div>
  );
}

// ── events ───────────────────────────────────────────────────────────────────

export function AddEventDialog({ onClose, onAdded }) {
  const { meta, reloadMeta, notify, refresh } = useIt();
  const [f, setF] = useState({ name: '', kind: 'onsite', starts_on: '', ends_on: '', store_id: '', venue: '' });
  const set = (k) => (v) => setF((prev) => ({ ...prev, [k]: v }));
  const { busy, error, run } = useAction();
  const save = () => run(async () => {
    const r = await itApi.addEvent({ ...f, store_id: Number(f.store_id), ends_on: f.ends_on || null });
    await reloadMeta(); refresh(); notify('Event added');
    onAdded && onAdded(r.id);
    onClose();
  });
  const storeOptions = [{ label: 'Choose a store', value: '' }, ...stores(meta).map((l) => ({ label: l.name, value: String(l.id) }))];
  const endsBeforeStart = !!(f.ends_on && f.starts_on && f.ends_on < f.starts_on);
  const ready = f.name.trim() && f.starts_on && f.store_id && !endsBeforeStart && (f.kind === 'onsite' || f.venue.trim());
  return (
    <Modal title="Add an event" onClose={onClose} focusFirst footer={<>
      <Button onClick={onClose}>Cancel</Button>
      <Button variant="primary" loading={busy} disabled={!ready} onClick={save}>Save event</Button>
    </>}>
      {error && <Banner tone="critical"><p>{error}</p></Banner>}
      <FormLayout>
        <TextField label="Event name" value={f.name} onChange={set('name')} requiredIndicator autoComplete="off" />
        <FormLayout.Group condensed>
          <DateField label="Starts" value={f.starts_on} onChange={set('starts_on')} />
          <DateField label="Ends" value={f.ends_on} onChange={set('ends_on')} helpText={endsBeforeStart ? 'Ends before it starts.' : 'Leave blank for a one-day event.'} />
        </FormLayout.Group>
        <FormLayout.Group condensed>
          <Select label="Where is it?" options={[{ label: 'At one of our stores', value: 'onsite' }, { label: 'At a temporary venue', value: 'offsite' }]} value={f.kind} onChange={set('kind')} />
          <Select label={f.kind === 'onsite' ? 'Store' : 'Counts toward which store'} options={storeOptions} value={f.store_id} onChange={set('store_id')} requiredIndicator />
        </FormLayout.Group>
        {f.kind === 'offsite' && <TextField label="Venue name" value={f.venue} onChange={set('venue')} placeholder="Crossroads Fields, San Carlos" requiredIndicator autoComplete="off" />}
      </FormLayout>
      <Text as="p" tone="subdued" variant="bodySm">At a store, the event counts toward that store. At a venue, pick the store responsible for the devices so they appear in that store’s numbers.</Text>
    </Modal>
  );
}

export function EventDialog({ event, onClose }) {
  const { openDialog, canManage, meta } = useIt();
  const [list, setList] = useState(null);
  const { busy, error } = useAction();
  const load = useCallback(() => itApi.assets({ event_id: event.id, status: 'loan' }).then((r) => setList(r.assets)), [event.id]);
  useEffect(() => { load(); }, [load]);
  const checkInAll = () => openDialog({ type: 'checkin', assets: list });
  return (
    <Modal title={event.name} onClose={onClose} wide footer={<>
      <span className="uh-ia-hint">When the event ends, check everything back in at once.</span><span className="uh-sp" />
      {canManage && <button type="button" className="uh-btn primary" disabled={busy || !list || !list.length} onClick={checkInAll}>Check in all ({list ? list.length : 0})</button>}
    </>}>
      <Alert>{error}</Alert>
      <dl className="uh-ia-kv">
        <dt>Dates</dt><dd>{event.starts_on === event.ends_on ? dateOnly(event.starts_on) : `${dateOnly(event.starts_on)} to ${dateOnly(event.ends_on)}`}</dd>
        <dt>Where</dt><dd><LocChip name={event.location} /> {event.kind === 'onsite' ? '(at the store)' : '(offsite venue)'}</dd>
        <dt>Counts toward</dt><dd><LocChip name={event.store} kind="store" /></dd>
      </dl>
      <h3 className="uh-h2">Devices lent for this event</h3>
      <div className="uh-tscroll uh-ia-box"><table className="uh-tbl">
        <thead><tr><th>Tag</th><th>Item</th><th>Lent to</th><th>Return date</th></tr></thead>
        <tbody>
          {(list || []).map((a) => (
            <tr key={a.id}><td><button type="button" className="uh-link uh-mono" onClick={() => openDialog({ type: 'asset', id: a.id })}>{tagText(a)}</button></td>
              <td>{a.model}</td><td><WhoCell a={a} /></td><td>{dueBadge(a, meta.today)}</td></tr>
          ))}
          {list && !list.length && <tr><td colSpan={4}><div className="uh-empty">No devices out for this event.</div></td></tr>}
          {!list && <tr><td colSpan={4}><div className="uh-empty">Loading…</div></td></tr>}
        </tbody>
      </table></div>
    </Modal>
  );
}

// ── check out, check in, move ───────────────────────────────────────────────

export function CheckoutDialog({ assets, kind: startKind, onClose }) {
  const { meta, refresh, notify } = useIt();
  const first = assets[0];
  const [kind, setKind] = useState(startKind || (['deployed', 'repair'].includes(first.status) ? 'deploy' : 'lend'));
  // a device that already has a holder keeps them to start with, so turning a loan into a long-term assignment is one click
  const holder = (assets.length === 1 ? (meta.people.find((p) => p.id === first.holder_person_id)
    || meta.people.find((p) => !p.left_on && first.holder && p.name.toLowerCase() === first.holder.toLowerCase())) : null) || null;
  const [person, setPerson] = useState(holder);
  const [loc, setLoc] = useState(first.location_id || '');
  const [store, setStore] = useState(first.store_id || (first.location_kind === 'store' ? first.location_id : '') || '');
  const [eventId, setEventId] = useState('');
  const [due, setDue] = useState('');
  const [purpose, setPurpose] = useState('');
  const [addEvent, setAddEvent] = useState(false);
  const { busy, error, run } = useAction();
  const openEvents = meta.events.filter((e) => dateOnly(e.ends_on) >= meta.today);
  const pickEvent = (v) => {
    if (v === 'new') { setAddEvent(true); return; }
    setEventId(v);
    const ev = meta.events.find((e) => String(e.id) === String(v));
    if (ev) {
      setLoc(ev.location_id); setStore(ev.store_id);
      const d = new Date(dateOnly(ev.ends_on)); d.setDate(d.getDate() + 1);
      setDue(d.toISOString().slice(0, 10));
    }
  };
  const ev = meta.events.find((e) => String(e.id) === String(eventId));
  const useOnly = kind === 'deploy' && !person && purpose.trim() !== '';     // a device for a place and a use, like Register 2
  const need = kind === 'lend' && !due;
  const go = () => run(async () => {
    await itApi.checkout({ asset_ids: assets.map((a) => a.id), kind, person_id: person ? person.id : null, location_id: loc ? Number(loc) : null, store_id: store ? Number(store) : null,
      event_id: eventId ? Number(eventId) : null, due_on: kind === 'lend' ? due : null, purpose });
    notify(`${kind === 'lend' ? 'Lent' : 'Deployed'} ${plural(assets.length, 'device', 'devices')} ${person ? `to ${person.name}` : `for ${purpose.trim()}`}`);
    refresh(); onClose();
  });
  return (
    <Modal title={assets.length > 1 ? `Check out ${assets.length} devices` : `Check out ${tagText(first)}`} subtitle={assets.length > 1 ? assets.map(tagText).join(', ') : first.model}
      onClose={onClose} footer={<>
        <button type="button" className="uh-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="uh-btn primary" disabled={busy || (!person && !useOnly) || need} onClick={go}>Check out</button>
      </>}>
      <Alert>{error}</Alert>
      <div className="uh-ia-frow">
        <Field label="Lend or deploy to" required={kind !== 'deploy'} hint={kind === 'deploy' ? 'Pick a person, or leave this empty for a device that belongs to a place and fill in Used for below' : undefined}><PersonPicker value={person} onChange={setPerson} initial={holder ? holder.name : ''} /></Field>
        <Field label="Action">
          <select className="uh-select" value={kind} onChange={(e) => setKind(e.target.value)}><option value="lend">Lend (temporary)</option><option value="deploy">Deploy (long term)</option></select>
        </Field>
        {kind === 'lend' && <Field label="Return date" required><input className="uh-ia-inp" type="date" value={due} onChange={(e) => setDue(e.target.value)} /></Field>}
        <Field label="For an event?">
          <select className="uh-select" value={eventId} onChange={(e) => pickEvent(e.target.value)}>
            <option value="">No event</option>{openEvents.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}<option value="new">+ Add an event…</option>
          </select>
        </Field>
        <Field label="Where is it now">
          <select className="uh-select" value={loc} onChange={(e) => setLoc(e.target.value)}><option value="">Leave as it is</option>{meta.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select>
        </Field>
      </div>
      {ev && <div className="uh-ia-hint">It will be at {ev.location} and count toward {ev.store}. The return date defaults to the day after the event ends.</div>}
      <Field label={useOnly || (kind === 'deploy' && !person) ? 'Used for (required with no person)' : 'Purpose or use (a note)'}><input className="uh-ia-inp" value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="Events, replacement, Register 2, pressing portal" /></Field>
      {addEvent && <AddEventDialog onClose={() => setAddEvent(false)} onAdded={(id) => { setAddEvent(false); setTimeout(() => pickEvent(String(id)), 0); }} />}
    </Modal>
  );
}

export function MoveDialog({ assets, onClose }) {
  const { meta, refresh, notify } = useIt();
  const one = assets.length === 1 ? assets[0] : null;
  const withPerson = assets.filter((a) => a.holder_person_id);
  const [clearPerson, setClearPerson] = useState(false);
  const allStock = assets.every((a) => a.status === 'stock');                  // available devices: only the place changes, they stay available
  const noPerson = !allStock && (clearPerson || assets.some((a) => !a.holder_person_id));   // only these have a use of their own
  const [loc, setLoc] = useState(one && one.location_id ? String(one.location_id) : '');
  const [use, setUse] = useState(one ? (one.holder_person_id ? (one.purpose || '') : (one.holder_note || '')) : '');
  const { busy, error, run } = useAction();
  const go = () => run(async () => { await itApi.move({ asset_ids: assets.map((a) => a.id), location_id: loc ? Number(loc) : null, store_id: null, use: noPerson && (one || use.trim()) ? use : null, clear_person: clearPerson }); notify(allStock ? `Moved ${plural(assets.length, 'device', 'devices')}. Still available` : `Updated ${plural(assets.length, 'device', 'devices')}`); refresh(); onClose(); });
  return (
    <Modal title={allStock ? `Change location of ${plural(assets.length, 'device', 'devices')}` : `Move or change use of ${plural(assets.length, 'device', 'devices')}`} subtitle={assets.map(tagText).join(', ')} onClose={onClose} footer={<>
      <button type="button" className="uh-btn" onClick={onClose}>Cancel</button>
      <button type="button" className="uh-btn primary" disabled={busy || (allStock ? !loc || (!!one && loc === String(one.location_id || '')) : (!loc && !use.trim() && !clearPerson))} onClick={go}>Save</button>
    </>}>
      <Alert>{error}</Alert>
      <div className="uh-ia-frow">
        <Field label="Location" hint={allStock ? 'The device stays available; only where it is changes.' : undefined}><select className="uh-select" value={loc} onChange={(e) => setLoc(e.target.value)}><option value="">Leave as it is</option>{meta.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select></Field>
        {!allStock && withPerson.length > 0 && <label className="uh-ia-check"><input type="checkbox" checked={clearPerson} onChange={(e) => setClearPerson(e.target.checked)} /> {one ? `Take ${one.person || one.holder} off this device: it belongs to the place, not a person` : `Take the person off the ${withPerson.length} devices that have one`}</label>}
        {noPerson && <Field label="Used for" hint="For a device that belongs to a place, not a person. Devices held by a person keep that person."><input className="uh-ia-inp" value={use} onChange={(e) => setUse(e.target.value)} placeholder="Pressing portal, Register 2" /></Field>}
      </div>
    </Modal>
  );
}

// Every way a device comes back (off loan, unassigned, found again) asks where it is now, so it does not keep the place it was lent to.
export function CheckInDialog({ assets, onClose }) {
  const { meta, refresh, notify } = useIt();
  const [loc, setLoc] = useState('');                       // '' = not chosen yet, 'keep' = leave each where it is
  const { busy, error, run } = useAction();
  const here = [...new Set(assets.map((a) => a.location).filter(Boolean))];
  const gone = assets.every((a) => GONE.includes(a.status));
  const title = gone ? 'Back in stock' : assets.every((a) => a.status === 'loan') ? 'Check in' : assets.every((a) => a.status === 'deployed') ? 'Unassign' : 'Check in';
  const go = () => run(async () => {
    await itApi.checkin(assets.map((a) => a.id), loc === 'keep' ? null : Number(loc));
    notify(`${plural(assets.length, 'device', 'devices')} back in stock${loc && loc !== 'keep' ? ` at ${meta.locations.find((l) => String(l.id) === loc)?.name}` : ''}`);
    refresh(); onClose();
  });
  return (
    <Modal title={`${title}: ${plural(assets.length, 'device', 'devices')}`} subtitle={assets.map(tagText).join(', ')} onClose={onClose} footer={<>
      <button type="button" className="uh-btn" onClick={onClose}>Cancel</button>
      <button type="button" className="uh-btn primary" disabled={busy || !loc} onClick={go}>{title}</button>
    </>}>
      <Alert>{error}</Alert>
      <div className="uh-ia-frow">
        <Field label="Where is it now" required hint={here.length ? `Recorded at ${here.join(', ')}.` : 'No location recorded.'}>
          <select className="uh-select" value={loc} onChange={(e) => setLoc(e.target.value)}>
            <option value="">Choose where it is…</option>
            <option value="keep">Leave {assets.length === 1 ? 'it' : 'them'} where {assets.length === 1 ? 'it is' : 'they are'} recorded</option>
            {meta.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </Field>
      </div>
    </Modal>
  );
}

export function StatusDialog({ asset, onClose }) {
  const { meta, refresh, notify } = useIt();
  const [status, setStatus] = useState('lost');
  const [reason, setReason] = useState(meta.disposal_reasons[0]);
  const [on, setOn] = useState(meta.today);
  const [note, setNote] = useState('');
  const { busy, error, run } = useAction();
  const go = () => run(async () => { await itApi.setStatus(asset.id, { status, reason: status === 'disposed' ? reason : '', on, note }); notify(`${tagText(asset)} marked ${status}`); refresh(); onClose(); });
  return (
    <Modal title={`Mark ${tagText(asset)} as lost, stolen or disposed`} onClose={onClose} footer={<>
      <button type="button" className="uh-btn" onClick={onClose}>Cancel</button>
      <button type="button" className="uh-btn primary" disabled={busy} onClick={go}>Save</button>
    </>}>
      <Alert>{error}</Alert>
      <div className="uh-ia-frow">
        <Field label="What happened"><select className="uh-select" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="lost">Lost</option><option value="stolen">Stolen</option><option value="disposed">Disposed of</option><option value="retired">Retired (still owned)</option></select></Field>
        {status === 'disposed' && <Field label="How it left"><select className="uh-select" value={reason} onChange={(e) => setReason(e.target.value)}>{meta.disposal_reasons.map((r) => <option key={r}>{r}</option>)}</select></Field>}
        <Field label="Date"><input className="uh-ia-inp" type="date" value={on} onChange={(e) => setOn(e.target.value)} /></Field>
      </div>
      <Field label="Notes"><input className="uh-ia-inp" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Where it was last seen, police report number, who took it" /></Field>
      <div className="uh-ia-hint">The device stays in the system with its full history. It leaves the Available and Loans lists.</div>
    </Modal>
  );
}

function ReceiptPicker({ value, onChange }) {
  const [q, setQ] = useState('');
  const [found, setFound] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => itApi.receipts({ q, sort: 'date', dir: 'desc' }).then((r) => setFound(r.receipts.slice(0, 8))), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="uh-ia-rpick">
      <input className="uh-ia-inp" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by vendor, order number, card, date or R-number" autoFocus />
      <div className="uh-ia-rlist" role="listbox" aria-label="Receipts">
        {found.map((r) => (
          <button type="button" key={r.id} className={value === r.id ? 'on' : ''} aria-pressed={value === r.id} onClick={() => onChange(r.id)}>
            <span className="uh-mono nm">{r.code}</span><span>{r.vendor || 'No vendor'}</span><span>{dateOnly(r.purchased_on)}</span><span className="uh-faint">{r.order_number}</span>
            <span className="r uh-num">{money(r.total)}</span><span className="uh-faint">{plural(r.items, 'item', 'items')}</span>
          </button>
        ))}
        {!found.length && <div className="uh-empty">No receipts match.</div>}
      </div>
    </div>
  );
}

export function LinkReceiptDialog({ assets, onClose }) {
  const { refresh, notify, openDialog } = useIt();
  const [rid, setRid] = useState(null);
  const { busy, error, run } = useAction();
  const go = () => run(async () => { await itApi.linkReceipt(assets.map((a) => a.id), rid); notify(`Linked ${plural(assets.length, 'asset', 'assets')} to the receipt`); refresh(); onClose(); });
  return (
    <Modal title={`Link ${plural(assets.length, 'asset', 'assets')} to a receipt`} subtitle={assets.slice(0, 4).map(tagText).join(', ') + (assets.length > 4 ? '…' : '')} onClose={onClose} wide footer={<>
      <button type="button" className="uh-link" onClick={() => openDialog({ type: 'addReceipt' })}>Or add a new purchase</button><span className="uh-sp" />
      <button type="button" className="uh-btn" onClick={onClose}>Cancel</button>
      <button type="button" className="uh-btn primary" disabled={busy || !rid} onClick={go}>Link</button>
    </>}>
      <Alert>{error}</Alert>
      <ReceiptPicker value={rid} onChange={setRid} />
      <div className="uh-ia-hint">Their price stays as it is. A receipt can hold any number of assets.</div>
    </Modal>
  );
}

export function AddAssetsDialog({ receipt, onClose }) {
  const { refresh, notify, openDialog } = useIt();
  const [q, setQ] = useState('');
  const [list, setList] = useState(null);
  const [sel, setSel] = useState({});
  const { busy, error, run } = useAction();
  useEffect(() => {
    const t = setTimeout(() => itApi.assets({ q, no_receipt: true }).then((r) => setList(r.assets)), 250);
    return () => clearTimeout(t);
  }, [q]);
  const ids = Object.keys(sel).filter((k) => sel[k]).map(Number);
  const go = () => run(async () => { await itApi.linkReceipt(ids, receipt.id); notify(`Added ${plural(ids.length, 'asset', 'assets')} to ${receipt.code}`); refresh(); onClose(); openDialog({ type: 'receipt', id: receipt.id }); });
  return (
    <Modal title={`Add assets to ${receipt.code}`} subtitle={`${receipt.vendor || 'No vendor'} · ${dateOnly(receipt.purchased_on)}`} onClose={onClose} wide footer={<>
      <span className="uh-ia-hint">{ids.length} selected</span><span className="uh-sp" />
      <button type="button" className="uh-btn" onClick={onClose}>Cancel</button>
      <button type="button" className="uh-btn primary" disabled={busy || !ids.length} onClick={go}>Add to this receipt</button>
    </>}>
      <Alert>{error}</Alert>
      <input className="uh-ia-inp" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search assets with no receipt: tag, model, serial or person" autoFocus />
      <div className="uh-tscroll uh-ia-box" style={{ maxHeight: 360, overflow: 'auto' }}><table className="uh-tbl">
        <thead><tr><th style={{ width: 34 }} /><th>Tag</th><th>Type</th><th>Model</th><th>Who / use</th><th className="r">Price</th></tr></thead>
        <tbody>
          {(list || []).map((a) => (
            <tr key={a.id} className="click" onClick={() => setSel({ ...sel, [a.id]: !sel[a.id] })}>
              <td><input type="checkbox" aria-label={`Select ${tagText(a)}`} checked={!!sel[a.id]} onChange={() => {}} /></td>
              <td className="uh-mono nm uh-ia-nowrap">{a.tag || <span className="uh-faint">No tag yet</span>}</td><td>{a.type}</td><td>{a.model}</td><td><WhoCell a={a} /></td><td className="r uh-num">{money(a.price)}</td>
            </tr>
          ))}
          {list && !list.length && <tr><td colSpan={6}><div className="uh-empty">No unlinked assets match.</div></td></tr>}
          {!list && <tr><td colSpan={6}><div className="uh-empty">Loading…</div></td></tr>}
        </tbody>
      </table></div>
      <div className="uh-ia-hint">Only assets that have no receipt yet are listed (the first 100 matches). To move an asset from another receipt, open it and use Change receipt.</div>
    </Modal>
  );
}

export function DeleteDialog({ assets, onClose }) {
  const { refresh, notify } = useIt();
  const [sure, setSure] = useState(false);
  const { busy, error, run } = useAction();
  const out = assets.filter((a) => a.status === 'loan' || a.status === 'deployed');
  const go = () => run(async () => { await itApi.deleteAssets(assets.map((a) => a.id)); notify(`Deleted ${plural(assets.length, 'asset', 'assets')}`); refresh(); onClose(); });
  return (
    <Modal title={`Delete ${plural(assets.length, 'asset', 'assets')} permanently`} onClose={onClose} footer={<>
      <button type="button" className="uh-btn" onClick={onClose}>Cancel</button>
      <button type="button" className="uh-btn danger" disabled={busy || !sure} onClick={go}>Delete {assets.length > 1 ? `${assets.length} assets` : 'asset'}</button>
    </>}>
      <Alert>{error}</Alert>
      <p>This removes the {assets.length > 1 ? 'assets and their' : 'asset and its'} history for good. Receipts stay, but they no longer list {assets.length > 1 ? 'them' : 'it'}. If you only want {assets.length > 1 ? 'them' : 'it'} out of circulation, mark {assets.length > 1 ? 'them' : 'it'} as disposed or retired instead, which keeps the history.</p>
      <ul className="uh-ia-dellist">{assets.slice(0, 12).map((a) => <li key={a.id}><span className="uh-mono">{tagText(a)}</span> {a.model} <StatusBadge status={a.status} /></li>)}{assets.length > 12 && <li className="uh-faint">and {assets.length - 12} more</li>}</ul>
      {out.length > 0 && <div className="uh-ia-warn">{plural(out.length, 'of these is', 'of these are')} still deployed or on loan.</div>}
      <label className="uh-ia-check"><input type="checkbox" checked={sure} onChange={(e) => setSure(e.target.checked)} /> I understand this cannot be undone.</label>
    </Modal>
  );
}

export function EditDialog({ asset, onClose }) {
  const { meta, refresh, notify } = useIt();
  const [f, setF] = useState({ model: asset.model, serial: asset.serial || '', hostname: asset.hostname || '', tag: asset.tag || '', type: asset.type, notes: asset.notes || '', holder_note: asset.holder_note || '' });
  const [specs, setSpecs] = useState(asset.specs || {});
  const [clear, setClear] = useState(false);
  const { busy, error, run } = useAction();
  const fields = meta.specs[f.type] || [];
  const hasHostname = (meta.hostname_types || []).includes(f.type);        // laptops and desktops
  const go = () => run(async () => {
    const body = { model: f.model, serial: f.serial, type: f.type, notes: f.notes, holder_note: f.holder_note, specs: Object.fromEntries(fields.map((k) => [k, specs[k] || ''])), clear_review: clear };
    if (hasHostname) body.hostname = f.hostname.trim();
    if (f.tag.trim().toUpperCase() !== (asset.tag || '')) body.tag = f.tag.trim();     // an empty box clears the tag
    await itApi.editAsset(asset.id, body);
    notify('Details saved'); refresh(); onClose();
  });
  return (
    <Modal title={`Edit details for ${tagText(asset)}`} onClose={onClose} footer={<>
      <button type="button" className="uh-btn" onClick={onClose}>Cancel</button>
      <button type="button" className="uh-btn primary" disabled={busy} onClick={go}>Save</button>
    </>}>
      <Alert>{error}</Alert>
      <div className="uh-ia-frow">
        <Field label="Model"><input className="uh-ia-inp" value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} /></Field>
        <Field label="Type"><select className="uh-select" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>{meta.types.map((t) => <option key={t}>{t}</option>)}</select></Field>
        <Field label="SP tag" hint="The pre-printed barcode, like SP-1234. Empty the box to take the tag off this device."><input className="uh-ia-inp uh-mono" value={f.tag} onChange={(e) => setF({ ...f, tag: e.target.value })} placeholder="SP-0000" /></Field>
        <Field label="Serial number"><input className="uh-ia-inp uh-mono" value={f.serial} onChange={(e) => setF({ ...f, serial: e.target.value })} /></Field>
        {hasHostname && <Field label="Hostname" hint="SoccerPost followed by the number, like SoccerPost82. Empty the box to clear it."><input className="uh-ia-inp uh-mono" value={f.hostname} onChange={(e) => setF({ ...f, hostname: e.target.value })} placeholder="SoccerPost123" autoComplete="off" /></Field>}
      </div>
      {fields.length > 0 && <>
        <h3 className="uh-h2">{f.type} details</h3>
        <div className="uh-ia-frow">{fields.map((k) => <Field key={k} label={k}><input className="uh-ia-inp" value={specs[k] || ''} onChange={(e) => setSpecs({ ...specs, [k]: e.target.value })} /></Field>)}</div>
      </>}
      {!asset.holder_person_id && <Field label="Used for" hint="For a device that belongs to a place, not a person, such as Register 2 or Pressing portal"><input className="uh-ia-inp" value={f.holder_note} onChange={(e) => setF({ ...f, holder_note: e.target.value })} /></Field>}
      <Field label="Notes"><textarea className="uh-ia-inp" rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
      {asset.needs_review && <label className="uh-ia-check"><input type="checkbox" checked={clear} onChange={(e) => setClear(e.target.checked)} /> Mark as checked ({asset.needs_review})</label>}
    </Modal>
  );
}

// ── one asset ────────────────────────────────────────────────────────────────

function LoginBlock({ asset }) {
  const { isAdmin } = useIt();
  const [shown, setShown] = useState(null);
  const [edit, setEdit] = useState(false);
  const [text, setText] = useState('');
  const { busy, error, run } = useAction();
  const reveal = () => run(async () => { const r = await itApi.revealLogin(asset.id); setShown(r.login); });
  const save = () => run(async () => { await itApi.saveLogin(asset.id, text); setEdit(false); setShown(null); asset.has_login = !!text.trim(); });
  return (
    <div className="uh-ia-later">
      <b>Device login</b> <span className="uh-muted">(for locked-out shared devices)</span>
      <Alert>{error}</Alert>
      <div className="uh-ia-login">
        {!asset.has_login && !edit && <span className="uh-faint">No login saved.</span>}
        {asset.has_login && !isAdmin && <StatusPill tone="neu">Login saved. Ask an admin.</StatusPill>}
        {asset.has_login && isAdmin && !edit && (
          <>
            <span className="uh-mono" style={shown ? undefined : { letterSpacing: '.12em' }}>{shown ?? '••••••••••••'}</span>
            {shown === null
              ? <button type="button" className="uh-link" disabled={busy} onClick={reveal}>Reveal</button>
              : <button type="button" className="uh-link" onClick={() => setShown(null)}>Hide</button>}
          </>
        )}
        {isAdmin && !edit && <button type="button" className="uh-link" onClick={() => { setEdit(true); setText(shown || ''); }}>{asset.has_login ? 'Change' : 'Add a login'}</button>}
      </div>
      {edit && (
        <div className="uh-ia-login-edit">
          <textarea className="uh-ia-inp" rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Shared login or PIN for this device. Leave empty to remove." />
          <div className="uh-ia-row"><button type="button" className="uh-btn slim primary" disabled={busy} onClick={save}>Save</button><button type="button" className="uh-btn slim" onClick={() => setEdit(false)}>Cancel</button></div>
        </div>
      )}
      <div className="uh-ia-hint">Only admins can reveal it. Each reveal is recorded in the activity log. Never shown in lists, search or exports.</div>
    </div>
  );
}

export function AssetDialog({ id, onClose }) {
  const { nameOf } = useAuth();
  const { meta, canManage, openDialog, refresh } = useIt();
  const [a, setA] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(() => itApi.asset(id).then(setA, (e) => setError(e.message)), [id]);
  useEffect(() => { load(); }, [load]);
  if (!a) return <Modal title="Asset" onClose={onClose}>{error ? <Alert>{error}</Alert> : <div className="uh-empty">Loading…</div>}</Modal>;
  const gone = GONE.includes(a.status);
  const specs = Object.entries(a.specs || {}).filter(([, v]) => v);
  const act = (type) => () => { openDialog({ type, assets: [a], asset: a }); };
  return (
    <Modal title={tagText(a)} subtitle={`${a.type} · ${a.model || a.hostname || 'no model recorded'}`} onClose={onClose} wide footer={canManage && <>
      <button type="button" className="uh-btn" onClick={act('edit')}>Edit details</button>
      {!gone && <button type="button" className="uh-btn" onClick={act('status')}>Lost, stolen or disposed…</button>}
      <button type="button" className="uh-btn" onClick={act('delete')}>Delete…</button>
      <span className="uh-sp" />
      {a.status === 'loan' && <button type="button" className="uh-btn" onClick={() => openDialog({ type: 'checkout', assets: [a], kind: 'deploy' })}>Change to deployed…</button>}
      {a.status === 'loan' && <button type="button" className="uh-btn primary" onClick={act('checkin')}>Check in</button>}
      {a.status === 'stock' && <button type="button" className="uh-btn" onClick={act('move')}>Change location…</button>}
      {a.status === 'stock' && <button type="button" className="uh-btn primary" onClick={act('checkout')}>Check out</button>}
      {gone && <button type="button" className="uh-btn" onClick={act('checkin')}>Found or restored: back in stock</button>}
      {a.status === 'deployed' && <button type="button" className="uh-btn" onClick={act('checkin')}>Unassign: back in stock</button>}
      {(a.status === 'deployed' || a.status === 'repair') && <button type="button" className="uh-btn" onClick={act('move')}>Move or change use…</button>}
      {(a.status === 'deployed' || a.status === 'repair') && <button type="button" className="uh-btn" onClick={act('checkout')}>Reassign to a person…</button>}
    </>}>
      <div className="uh-ia-badges"><StatusBadge status={a.status} />{a.needs_review && <StatusPill tone="hold">Needs review</StatusPill>}</div>
      {a.needs_review && <div className="uh-ia-warn">{a.needs_review}</div>}
      <div className="uh-ia-cols">
        <dl className="uh-ia-kv">
          <dt>Serial number</dt><dd className="uh-mono">{a.serial || <span className="uh-faint">none recorded</span>}</dd>
          {(meta.hostname_types || []).includes(a.type) && <><dt>Hostname</dt><dd className="uh-mono">{a.hostname || <span className="uh-faint">none recorded</span>}</dd></>}
          {specs.map(([k, v]) => <React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>)}
          <dt>Where</dt><dd><WhereCell a={a} /></dd>
          <dt>Who / use</dt><dd>{a.holder || <span className="uh-faint">nobody</span>}</dd>
          {a.event_name && <><dt>Event</dt><dd>{a.event_name}</dd></>}
          {a.status === 'loan' && <><dt>Due back</dt><dd>{dueBadge(a, meta.today)}</dd></>}
          {a.purpose && <><dt>Purpose</dt><dd>{a.purpose}</dd></>}
          <dt>Price</dt><dd>{a.price == null ? <span className="uh-faint">none</span> : money(a.price)}</dd>
          {a.notes && <><dt>Notes</dt><dd className="uh-ia-pre">{a.notes}</dd></>}
        </dl>
        <div className="uh-ia-col">
          <div><h3 className="uh-h2">Receipt</h3>
            {a.receipt
              ? <div><button type="button" className="uh-link" onClick={() => openDialog({ type: 'receipt', id: a.receipt.id })}>R-{String(a.receipt.id).padStart(4, '0')} · {a.receipt.vendor || 'no vendor'} · {dateOnly(a.receipt.purchased_on)}</button>
                {!a.receipt.has_file && <div><StatusPill tone="hold">No file attached</StatusPill></div>}
                {canManage && <div className="uh-ia-row"><button type="button" className="uh-link" onClick={act('link')}>Change receipt</button>
                  <button type="button" className="uh-link" onClick={async () => { await itApi.linkReceipt([a.id], null); load(); refresh(); }}>Unlink</button></div>}</div>
              : <div><StatusPill tone="hold">No receipt attached</StatusPill>
                {canManage && <div><button type="button" className="uh-link" onClick={act('link')}>Link to a receipt</button></div>}</div>}
          </div>
          <LoginBlock asset={a} />
        </div>
      </div>
      <div><h3 className="uh-h2">History</h3>
        <ul className="uh-ia-tl">
          {a.history.map((h, i) => <li key={i} className={h.kind}><span className="pt" /><span>{h.text}<div className="w">{when(h.at)}{h.by ? ` · ${nameOf(h.by)}` : ''}</div></span></li>)}
          {!a.history.length && <li><span className="pt" /><span className="uh-faint">No history yet.</span></li>}
        </ul>
      </div>
    </Modal>
  );
}

// ── receipts ─────────────────────────────────────────────────────────────────

export function ReceiptDialog({ id, onClose }) {
  const { canManage, openDialog, refresh, reloadMeta } = useIt();
  const [r, setR] = useState(null);
  const [digits, setDigits] = useState('');
  const [editing, setEditing] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const { busy, error, run } = useAction();
  const load = useCallback(() => itApi.receipt(id).then(setR), [id]);
  useEffect(() => { load(); }, [load]);
  if (!r) return <Modal title="Receipt" onClose={onClose}><div className="uh-empty">Loading…</div></Modal>;
  const lines = r.lines || [];
  const sum = r.assets.reduce((s, a) => s + (Number(a.price) || 0), 0) + lines.reduce((s, n) => s + n.qty * (Number(n.price) || 0), 0);
  const tax = Number(r.tax) || 0;
  const fees = chargesTotal(r.charges);
  const ok = r.total != null && Math.abs(sum + tax + fees - Number(r.total)) < 1;
  const saveDigits = () => run(async () => { await itApi.setCardDigits(r.card_id, { last4: digits, holder: '' }); setDigits(''); await reloadMeta(); await load(); refresh(); });
  return (
    <Modal title={r.code} subtitle={`${r.vendor || 'No vendor'} · ${dateOnly(r.purchased_on)}`} onClose={onClose} wide footer={canManage ? <>
      {(r.files || []).some((f) => f.kind === 'upload') && <button type="button" className="uh-btn" onClick={() => setExtracting(true)}>Extract items</button>}
      <button type="button" className="uh-btn primary" onClick={() => setEditing(true)}>Edit receipt</button>
    </> : undefined}>
      <Alert>{error}</Alert>
      {extracting && <ExtractItemsDialog receipt={r} onClose={() => setExtracting(false)} onSaved={() => { setExtracting(false); load(); refresh(); }} />}
      {editing && <EditReceiptDialog receipt={r} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); refresh(); }} />}
      <div className="uh-ia-rcpt-top">
      <dl className="uh-ia-kv">
        <dt>Order #</dt><dd className="uh-mono">{r.order_number || <span className="uh-faint">none</span>}</dd>
        <dt>Payment card</dt><dd>{r.card}{r.card_id && !r.last4 && canManage && (
          <span className="uh-ia-inline"> <input className="uh-ia-inp" style={{ width: 90 }} inputMode="numeric" maxLength={4} placeholder="Last 4" value={digits} onChange={(e) => setDigits(e.target.value.replace(/\D/g, ''))} />
            <button type="button" className="uh-btn slim" disabled={digits.length !== 4 || busy} onClick={saveDigits}>Save digits</button></span>)}</dd>
        <dt>Total</dt><dd>{r.total == null ? <span className="uh-faint">none</span> : money(r.total)}{r.tax != null && <span className="uh-faint"> including {money(r.tax)} tax{fees ? ` and ${money(fees)} in charges` : ''}</span>}</dd>
        {(r.charges || []).length > 0 && <><dt>Charges</dt><dd>{r.charges.map((c, i) => <span key={i} className="uh-ia-chip">{c.label} {money(c.amount)}</span>)}</dd></>}
        <dt>Receipt</dt>
        <dd><StatusPill tone={r.has_file ? 'ful' : 'hold'}>{pagesText(r.pages)}</StatusPill></dd>
        {r.notes && <><dt>Notes</dt><dd className="uh-ia-pre">{r.notes}</dd></>}
      </dl>
      </div>
      <ReceiptPages r={r} onChanged={() => { load(); refresh(); }} />
      <div className="uh-tscroll uh-ia-box"><table className="uh-tbl">
        <thead><tr><th>Tag</th><th>Item</th><th>Where</th><th className="r">Price</th></tr></thead>
        <tbody>
          {r.assets.map((a) => <tr key={a.id} className="click" onClick={() => openDialog({ type: 'asset', id: a.id })}><td className="uh-mono nm">{tagText(a)}</td><td>{a.model}</td><td><WhereCell a={a} /></td><td className="r uh-num">{money(a.price)}</td></tr>)}
          {!r.assets.length && <tr><td colSpan={4}><div className="uh-empty">No assets are attached to this receipt yet.</div></td></tr>}
        </tbody>
      </table></div>
      {canManage && <div className="uh-ia-row"><button type="button" className="uh-btn slim" onClick={() => openDialog({ type: 'addAssets', receipt: r })}>Add assets to this receipt</button></div>}
      {lines.length > 0 && (
        <>
          <h3 className="uh-h2">Not tracked as assets</h3>
          <div className="uh-tscroll uh-ia-box"><table className="uh-tbl">
            <thead><tr><th>Item</th><th>Type</th><th>Location</th><th className="r">Qty</th><th className="r">Unit price</th></tr></thead>
            <tbody>{lines.map((n) => <tr key={n.id}><td>{n.model}</td><td>{n.type}</td><td>{n.location}</td><td className="r uh-num">{n.qty}</td><td className="r uh-num">{money(n.price)}</td></tr>)}</tbody>
          </table></div>
        </>
      )}
      {r.allocation && r.allocation.length > 0 && (
        <>
          <h3 className="uh-h2">By location</h3>
          <div className="uh-tscroll uh-ia-box"><table className="uh-tbl">
            <thead><tr><th>Location</th><th className="r">Units</th><th className="r">Items</th><th className="r">Tax share</th>{fees ? <th className="r">Charges share</th> : null}<th className="r">Total</th></tr></thead>
            <tbody>
              {r.allocation.map((l) => (
                <tr key={l.location}><td className="nm">{l.location}</td><td className="r uh-num">{l.units}</td><td className="r uh-num">{money(l.items)}</td>
                  <td className="r uh-num">{l.tax == null ? <span className="uh-faint">no tax</span> : money(l.tax)}</td>{fees ? <td className="r uh-num">{money(l.charges)}</td> : null}<td className="r uh-num">{money(l.total)}</td></tr>
              ))}
              <tr className="uh-ia-sumrow"><td className="nm">All locations</td><td className="r uh-num">{r.allocation.reduce((n, l) => n + l.units, 0)}</td>
                <td className="r uh-num">{money(r.allocation.reduce((n, l) => n + l.items, 0))}</td>
                <td className="r uh-num">{r.tax == null ? <span className="uh-faint">no tax</span> : money(r.allocation.reduce((n, l) => n + (l.tax || 0), 0))}</td>
                {fees ? <td className="r uh-num">{money(r.allocation.reduce((n, l) => n + (l.charges || 0), 0))}</td> : null}
                <td className="r uh-num">{money(r.allocation.reduce((n, l) => n + l.total, 0))}</td></tr>
            </tbody>
          </table></div>
          <div className="uh-ia-hint">{r.tax == null ? 'Add the tax with Edit receipt to split it across locations. ' : `Tax${fees ? ' and charges are' : ' is'} split by item value: each device carries its share of the order. `}A device counts toward the store it is assigned to, otherwise where it is.</div>
        </>
      )}
      {(r.assets.length > 0 || lines.length > 0) && r.total != null && (
        <StatusPill tone={ok ? 'ful' : 'hold'}>Items{tax ? ` plus ${money(tax)} tax` : ''}{fees ? ` plus ${money(fees)} in charges` : ''} add up to {money(sum + tax + fees)} of {money(r.total)}{ok ? '' : ' (some prices may be missing, not split, or a charge may be missing)'}</StatusPill>
      )}
    </Modal>
  );
}

// Payment card options for the two receipt forms.
const cardOptions = (meta, withNew) => [
  { label: 'No card', value: '' },
  ...meta.cards.map((c) => ({ value: String(c.id), label: c.last4 ? `•••• ${c.last4}${c.holder ? ` (${c.holder})` : ''}` : (c.label || 'Unknown card') })),
  ...(withNew ? [{ label: '+ Add a payment card…', value: 'new' }] : []),
];
const isMoney = (v) => v === '' || !Number.isNaN(Number(v));

export function EditReceiptDialog({ receipt, onClose, onSaved }) {
  const { meta, reloadMeta, notify } = useIt();
  const [f, setF] = useState({ vendor: receipt.vendor || '', purchased_on: receipt.purchased_on ? String(receipt.purchased_on).slice(0, 10) : '', order_number: receipt.order_number || '',
    card_id: receipt.card_id ? String(receipt.card_id) : '', total: receipt.total == null ? '' : String(receipt.total), tax: receipt.tax == null ? '' : String(receipt.tax), notes: receipt.notes || '' });
  const known = (meta.vendors || []).includes(receipt.vendor || '');
  const [vendorPick, setVendorPick] = useState(receipt.vendor ? (known ? receipt.vendor : 'other') : '');
  const [newCard, setNewCard] = useState({ last4: '', holder: '' });
  const [items, setItems] = useState([...receipt.assets.map(fromAsset), ...(receipt.lines || []).map(fromLine)]);
  const [removed, setRemoved] = useState([]);
  const [defLoc, setDefLoc] = useState('');
  const [charges, setCharges] = useState(fromApiCharges(receipt.charges));
  const [newPages, setNewPages] = useState([]);           // files to upload on Save
  const [gonePages, setGonePages] = useState([]);           // ids of stored pages to take off on Save
  const [open, setOpen] = useState(null);
  const blobs = useStoredPages(receipt.id, receipt.files);
  const newUrls = useNewPages(newPages);
  const { busy, error, run } = useAction();
  const set = (k) => (v) => setF((prev) => ({ ...prev, [k]: v }));
  const vendorOptions = [{ label: 'Choose a vendor', value: '' }, ...(meta.vendors || []).map((v) => ({ label: v, value: v })), { label: 'Other vendor…', value: 'other' }];
  const pickVendor = (v) => { setVendorPick(v); set('vendor')(v === 'other' ? '' : v); };
  const pages = [...storedRows((receipt.files || []).filter((f) => !gonePages.includes(f.id)), blobs), ...newRows(newPages, newUrls)];
  const removePage = (p) => { if (p.kind === 'new') setNewPages((prev) => prev.filter((n) => n.key !== p.key)); else setGonePages((prev) => [...prev, p.id]); };
  const go = () => run(async () => {
    const body = { vendor: f.vendor, order_number: f.order_number, notes: f.notes };
    if (f.purchased_on) body.purchased_on = f.purchased_on;
    if (f.card_id === 'new') body.card_id = (await itApi.addCard(newCard)).id; else if (f.card_id) body.card_id = Number(f.card_id);
    if (f.total !== '') body.total = Number(f.total);
    if (f.tax !== '') body.tax = Number(f.tax);
    body.charges = toApiCharges(charges);
    await itApi.editReceipt(receipt.id, body);
    const r = await itApi.editReceiptItems(receipt.id, toEditBody(items, defLoc, removed));
    for (const id of gonePages) await itApi.removeReceiptPage(receipt.id, id);     // eslint-disable-line no-await-in-loop
    if (newPages.length) await itApi.uploadReceiptPages(receipt.id, newPages.map((n) => n.file));
    await reloadMeta();
    notify(`${receipt.code} saved${r.changed ? `, ${plural(r.changed, 'asset', 'assets')} updated` : ''}${r.added ? `, ${plural(r.added, 'asset', 'assets')} added` : ''}`);
    onSaved();
  });
  return (
    <Modal title={`Edit ${receipt.code}`} onClose={onClose} wide focusFirst footer={<>
      <Button onClick={onClose}>Cancel</Button>
      <Button variant="primary" loading={busy} disabled={(f.card_id === 'new' && newCard.last4.length !== 4) || ![f.total, f.tax].every(isMoney) || problems(items, defLoc) > 0} onClick={go}>Save</Button>
    </>}>
      {error && <Banner tone="critical"><p>{error}</p></Banner>}
      <FormLayout>
        <div>
          <Text as="span" variant="bodyMd">Receipt pages</Text>
          <PageStrip pages={pages} canEdit busy={busy} prefix={receipt.code} onAdd={(files) => setNewPages((prev) => [...prev, ...asNew(files)])} onRemove={removePage} onOpen={setOpen}
            hint={`Drop images or a PDF, paste them, or click to choose files.${gonePages.length ? ` ${gonePages.length === 1 ? 'One page comes' : `${gonePages.length} pages come`} off when you save.` : ''}`} />
          {open != null && pages[open] && <PageViewer pages={pages} index={open} name={`Receipt ${receipt.code}`} onClose={() => setOpen(null)} onIndex={setOpen} />}
        </div>
        <FormLayout.Group>
          <Select label="Vendor" options={vendorOptions} value={vendorPick} onChange={pickVendor} />
          {vendorPick === 'other' && <TextField label="Vendor name" value={f.vendor} onChange={set('vendor')} autoComplete="off" />}
          <DateField label="Date" value={f.purchased_on} onChange={set('purchased_on')} />
          <TextField label="Order number" value={f.order_number} onChange={set('order_number')} autoComplete="off" />
          <Select label="Payment card" options={cardOptions(meta, true)} value={f.card_id} onChange={set('card_id')} />
        </FormLayout.Group>
        {f.card_id === 'new' && (
          <FormLayout.Group condensed>
            <TextField label="Last 4 digits" value={newCard.last4} onChange={(v) => setNewCard({ ...newCard, last4: v.replace(/\D/g, '').slice(0, 4) })} inputMode="numeric" maxLength={4} helpText="Only the last 4 are stored. Never the full number." autoComplete="off" />
            <TextField label="Whose card" value={newCard.holder} onChange={(v) => setNewCard({ ...newCard, holder: v })} autoComplete="off" />
          </FormLayout.Group>
        )}
        <FormLayout.Group condensed>
          <TextField label="Total" value={f.total} onChange={set('total')} inputMode="decimal" prefix="$" placeholder="0.00" helpText="What was charged, tax included" autoComplete="off" error={isMoney(f.total) ? undefined : 'Enter a number'} />
          <TextField label="Tax" value={f.tax} onChange={set('tax')} inputMode="decimal" prefix="$" placeholder="0.00" autoComplete="off" error={isMoney(f.tax) ? undefined : 'Enter a number'} />
          <Select label="Location for new items" options={[{ label: 'Choose a location', value: '' }, ...meta.locations.map((l) => ({ label: l.name, value: String(l.id) }))]} value={defLoc} onChange={setDefLoc} helpText="Where items you add here go unless a line says otherwise" />
        </FormLayout.Group>
        <TextField label="Notes" value={f.notes} onChange={set('notes')} multiline={2} placeholder="Anything worth knowing about this purchase" autoComplete="off" />
      </FormLayout>
      <Text as="h3" variant="headingSm">Items on this receipt</Text>
      <ChargesEditor charges={charges} setCharges={setCharges} />
      <ItemsEditor items={items} setItems={setItems} defLoc={defLoc} tax={f.tax} total={f.total} charges={charges} onRemoveExisting={(id) => setRemoved((prev) => [...prev, id])} />
    </Modal>
  );
}

// Read the picture already attached to a receipt and add the devices and lines it shows, on top of what is linked. Never changes what is linked.
export function ExtractItemsDialog({ receipt, onClose, onSaved }) {
  const { meta, notify } = useIt();
  const [rc, setRc] = useState(null);
  const [items, setItems] = useState([]);
  const [defLoc, setDefLoc] = useState('');
  const { busy, error, setError, run } = useAction();
  useEffect(() => {
    itApi.readSavedReceipt(receipt.id).then((r) => { setRc(r); setItems(r.items.map(fromRead)); }).catch((e) => setError(e.message));
  }, [receipt.id]);       // eslint-disable-line react-hooks/exhaustive-deps
  const go = () => run(async () => {
    const r = await itApi.addReceiptItems(receipt.id, toApiItems(items, defLoc));
    notify(`Added ${plural(r.assets, 'asset', 'assets')}${r.lines ? ` and ${plural(r.lines, 'untracked line', 'untracked lines')}` : ''} to ${receipt.code}`);
    onSaved();
  });
  const linked = receipt.assets.length + (receipt.lines || []).reduce((n, l) => n + l.qty, 0);
  const differs = rc && rc.total != null && receipt.total != null && Math.abs(rc.total - Number(receipt.total)) >= 0.02;
  return (
    <Modal title={`Extract items from ${receipt.code}`} onClose={onClose} wide footer={<>
      <Button onClick={onClose}>Cancel</Button>
      <Button variant="primary" loading={busy} disabled={!rc || !countable(items).length || problems(items, defLoc) > 0} onClick={go}>Add items to {receipt.code}</Button>
    </>}>
      {error && <Banner tone="critical"><p>{error}</p></Banner>}
      {!rc && !error && <div className="uh-empty">Reading the receipt… this takes a few seconds.</div>}
      {rc && (
        <>
          <Banner tone="info"><p>The reader found {plural(rc.items.length, 'item', 'items')}. Nothing is added until you save. {linked > 0 ? `${plural(linked, 'unit is', 'units are')} already linked to this receipt and will not be changed; the items below are added on top, so remove any that are already there.` : ''}</p></Banner>
          {differs && <Banner tone="warning"><p>The receipt reads a total of {money(rc.total)}; this receipt says {money(receipt.total)}. Neither is changed here.</p></Banner>}
          <Select label="Location for these items" options={[{ label: 'Choose a location', value: '' }, ...meta.locations.map((l) => ({ label: l.name, value: String(l.id) }))]} value={defLoc} onChange={setDefLoc} helpText="Where the items go unless a line says otherwise" />
          <ItemsEditor items={items} setItems={setItems} defLoc={defLoc} tax={receipt.tax} total={receipt.total} charges={receipt.charges} />
        </>
      )}
    </Modal>
  );
}

export function AddReceiptDialog({ onClose }) {
  const { meta, reloadMeta, refresh, notify, openDialog } = useIt();
  const [f, setF] = useState({ vendor: '', purchased_on: '', order_number: '', card_id: '', total: '', tax: '' });
  const [vendorPick, setVendorPick] = useState('');     // a common vendor, or 'other' for a typed one
  const [items, setItems] = useState([blankItem()]);
  const [defLoc, setDefLoc] = useState('');             // where the units go unless a line says otherwise
  const [charges, setCharges] = useState([]);              // shipping, surcharges, tariffs, fees, discounts; the reader fills them in
  const [pagesNew, setPagesNew] = useState([]);           // the receipt's pages, uploaded after the purchase is saved
  const [open, setOpen] = useState(null);
  const newUrls = useNewPages(pagesNew);
  const [readInfo, setReadInfo] = useState(null);        // what the reader filled in, and its warnings
  const [reading, setReading] = useState(false);
  const [newCard, setNewCard] = useState({ last4: '', holder: '' });
  const { busy, error, setError, run } = useAction();
  const set = (k) => (v) => setF((prev) => ({ ...prev, [k]: v }));
  const vendorOptions = [{ label: 'Choose a vendor', value: '' }, ...(meta.vendors || []).map((v) => ({ label: v, value: v })), { label: 'Other vendor…', value: 'other' }];
  const pickVendor = (v) => { setVendorPick(v); set('vendor')(v === 'other' ? '' : v); };
  const pages = newRows(pagesNew, newUrls);
  const readReceipt = async () => {
    setReading(true); setError(''); setReadInfo(null);
    try {
      const rc = await itApi.readReceipt(pagesNew.map((n) => n.file));
      const filled = [];
      setF((prev) => {
        const next = { ...prev };
        [['purchased_on', rc.purchased_on], ['order_number', rc.order_number], ['total', rc.total], ['tax', rc.tax]].forEach(([k, v]) => {
          if (!next[k] && v != null && v !== '') { next[k] = String(v); filled.push(k); }
        });
        return next;
      });
      if (!f.vendor && rc.vendor) {
        const rv = rc.vendor.toLowerCase();
        const known = (meta.vendors || []).find((v) => rv === v.toLowerCase() || rv.includes(v.toLowerCase()) || v.toLowerCase().includes(rv));
        if (known) pickVendor(known); else { setVendorPick('other'); set('vendor')(rc.vendor); }
      }
      if (!charges.some((c) => c.label.trim()) && (rc.charges || []).length) setCharges(fromApiCharges(rc.charges));
      const found = rc.items.map(fromRead);
      setItems((prev) => (prev.every((it) => !it.model.trim() && it.price === '') ? (found.length ? found : prev) : [...prev, ...found]));
      setReadInfo({ count: rc.items.length, duplicates: rc.duplicates || [] });
    } catch (e) { setError(e.message); } finally { setReading(false); }
  };
  const real = countable(items);
  const go = () => run(async () => {
    const cardId = f.card_id === 'new' ? (await itApi.addCard(newCard)).id : (f.card_id ? Number(f.card_id) : null);
    const r = await itApi.addReceipt({
      vendor: f.vendor, purchased_on: f.purchased_on || null, order_number: f.order_number, card_id: cardId, total: f.total === '' ? null : Number(f.total), tax: f.tax === '' ? null : Number(f.tax),
      charges: toApiCharges(charges), file_url: null, items: toApiItems(items, defLoc),
    });
    if (pagesNew.length) await itApi.uploadReceiptPages(r.id, pagesNew.map((n) => n.file));
    await reloadMeta(); refresh(); notify(`Purchase saved with ${plural(r.assets, 'asset', 'assets')}${r.lines ? ` and ${plural(r.lines, 'untracked line', 'untracked lines')}` : ''}`);
    onClose(); openDialog({ type: 'receipt', id: r.id });
  });
  return (
    <Modal title="Add a purchase" onClose={onClose} wide focusFirst footer={<>
      <Button onClick={onClose}>Cancel</Button>
      <Button variant="primary" loading={busy} disabled={(f.card_id === 'new' && newCard.last4.length !== 4) || ![f.total, f.tax].every(isMoney) || problems(items, defLoc) > 0} onClick={go}>Save purchase</Button>
    </>}>
      {error && <Banner tone="critical"><p>{error}</p></Banner>}
      <FormLayout>
        <div>
          <Text as="span" variant="bodyMd">Receipt</Text>
          <PageStrip pages={pages} canEdit busy={reading || busy} prefix="Receipt" onAdd={(files) => { setPagesNew((prev) => [...prev, ...asNew(files)]); setReadInfo(null); }}
            onRemove={(p) => { setPagesNew((prev) => prev.filter((n) => n.key !== p.key)); setReadInfo(null); }} onOpen={setOpen}
            hint="Drop images or a PDF, paste them, or click to choose files. A long receipt can be several pictures; add them in reading order. Then choose Read receipt to fill in the rest." />
          {pagesNew.length > 0 && <div className="uh-ia-row"><Button variant="primary" loading={reading} onClick={readReceipt}>{readInfo ? 'Read again' : `Read receipt${pagesNew.length > 1 ? ` (${pagesNew.length} pages)` : ''}`}</Button></div>}
          {open != null && pages[open] && <PageViewer pages={pages} index={open} name="Receipt" onClose={() => setOpen(null)} onIndex={setOpen} />}
        </div>
        {readInfo && <Banner tone="info"><p>The reader found {plural(readInfo.count, 'item', 'items')}. Check everything it filled in, and fix anything that is wrong. Fields you had already filled in were left alone.</p></Banner>}
        {readInfo && readInfo.duplicates.length > 0 && <Banner tone="warning"><p>This order number is already on {readInfo.duplicates.join(', ')}. Check you are not adding the same purchase twice.</p></Banner>}
        <FormLayout.Group>
          <Select label="Vendor" options={vendorOptions} value={vendorPick} onChange={pickVendor} />
          {vendorPick === 'other' && <TextField label="Vendor name" value={f.vendor} onChange={set('vendor')} autoComplete="off" autoFocus />}
          <DateField label="Date" value={f.purchased_on} onChange={set('purchased_on')} />
          <TextField label="Order number" value={f.order_number} onChange={set('order_number')} autoComplete="off" />
          <Select label="Payment card" options={cardOptions(meta, true)} value={f.card_id} onChange={set('card_id')} />
        </FormLayout.Group>
        {f.card_id === 'new' && (
          <FormLayout.Group condensed>
            <TextField label="Last 4 digits" value={newCard.last4} onChange={(v) => setNewCard({ ...newCard, last4: v.replace(/\D/g, '').slice(0, 4) })} inputMode="numeric" maxLength={4} helpText="Only the last 4 are stored. Never the full number." autoComplete="off" />
            <TextField label="Whose card" value={newCard.holder} onChange={(v) => setNewCard({ ...newCard, holder: v })} autoComplete="off" />
          </FormLayout.Group>
        )}
        <FormLayout.Group condensed>
          <TextField label="Total" value={f.total} onChange={set('total')} inputMode="decimal" prefix="$" placeholder="0.00" helpText="What was charged, tax included" autoComplete="off" error={isMoney(f.total) ? undefined : 'Enter a number'} />
          <TextField label="Tax" value={f.tax} onChange={set('tax')} inputMode="decimal" prefix="$" placeholder="0.00" autoComplete="off" error={isMoney(f.tax) ? undefined : 'Enter a number'} />
          <Select label="Location for this purchase" options={[{ label: 'Choose a location', value: '' }, ...meta.locations.map((l) => ({ label: l.name, value: String(l.id) }))]} value={defLoc} onChange={setDefLoc} helpText="Where the items go unless a line says otherwise" />
        </FormLayout.Group>
      </FormLayout>
      <Text as="h3" variant="headingSm">Items bought on this receipt</Text>
      <ChargesEditor charges={charges} setCharges={setCharges} />
      <ItemsEditor items={items} setItems={setItems} defLoc={defLoc} tax={f.tax} total={real.length ? f.total : null} charges={charges} />
    </Modal>
  );
}

export function ExportDialog({ from: from0 = '', to: to0 = '', card: card0 = '', onClose }) {
  const { meta } = useIt();
  const [from, setFrom] = useState(from0);       // starts on whatever the Receipts tab is filtered to
  const [to, setTo] = useState(to0);
  const [card, setCard] = useState(card0);
  const { busy, error, run } = useAction();
  const go = () => run(async () => {
    const blob = await itApi.accountingCsv({ date_from: from, date_to: to, card_id: card });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = 'it-assets-accounting.csv'; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    onClose();
  });
  return (
    <Modal title="Export for accounting" onClose={onClose} footer={<>
      <button type="button" className="uh-btn" onClick={onClose}>Cancel</button>
      <button type="button" className="uh-btn primary" disabled={busy} onClick={go}>Download spreadsheet</button>
    </>}>
      <Alert>{error}</Alert>
      <div className="uh-ia-frow">
        <Field label="From"><input className="uh-ia-inp" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        <Field label="To"><input className="uh-ia-inp" type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        <Field label="Payment card"><select className="uh-select" value={card} onChange={(e) => setCard(e.target.value)}><option value="">All cards</option>
          {meta.cards.map((c) => <option key={c.id} value={c.id}>{c.last4 ? `•••• ${c.last4}${c.holder ? ` (${c.holder})` : ''}` : (c.label || 'Unknown card')}</option>)}</select></Field>
      </div>
      <div className="uh-ia-hint">One row per asset, so a receipt with ten items appears ten times. Accounting can sort or group by card, order number or receipt. Each row carries a link to the receipt.</div>
    </Modal>
  );
}
